/* Scroll reveal, sitewide.

   The reveal is per element, not per section. Each meaningful element on a page
   carries a `data-aos` attribute and this module is what turns those attributes
   into the reveal: it starts the library once, and re-runs its scan after each
   Inertia visit so the incoming page's elements start hidden and reveal as they
   are scrolled into view.

   The direction comes from where the element sits, not from taste, so related
   elements converge instead of every one of them arriving from the same edge.
   There are three cases and nothing else:

     vertical stack member    fade-up                   a section title, a card in
                                                         a grid, a list row, a table,
                                                         a band's content
     horizontal pair member   left  element fade-right   two columns, or one row of
                              right element fade-left    a zig-zag
     standalone media         zoom-in                   a cover, a screenshot, a
                                                         flyer, a mockup

   The pair case is the one worth stating out loud. `fade-right` on the left
   element means it starts to the left of its resting place and travels right, so
   the two halves of a pair move toward each other and meet in the middle. Giving
   both halves the same direction would make a pair read as a list.

   The media case is narrowed by the pair case rather than competing with it.
   Media that is one half of a pair follows the pair, so an offer poster beside
   its copy travels in from the right instead of zooming, and zoom-in is left for
   media that is the whole of a band's own content: a cover, a screenshot, a
   mockup.

   Motion is still one system. The travel, the duration and the easing are
   declared once in main.scss, beside the library's import, and the trigger
   offset is the one number in OPTIONS below. Only two things vary: the direction,
   because direction is the part that carries meaning, and the element's place in
   its line. Nothing carries its own duration or offset, so the reveal cannot
   drift one element at a time the way it would if each carried its own numbers.

   The reveal runs as a keyframe animation rather than the library's transition,
   and that is what makes the wait below safe to add. See the note in main.scss
   for why the transition had to go; the short version is that a transition put
   the reveal and a card's own hover lift into the same property, and one of the
   two always lost.

   The wait is the stagger, and it is the one thing an element carries. Elements
   that share a line on the page wait one step apart and arrive as a sweep;
   an element on its own line waits for nothing, because a vertical stack is
   already sequenced by the scroll that brings it in. `sequence` below decides
   only the places along a line, up to four of them. The step itself is
   $reveal-step in _tokens.scss, so no page picks its own tempo.

   The attribute never goes on an element whose ancestor also carries it. Two
   reveals in one subtree add their travel up and run in two stages, because the
   outer trigger always fires first. A `<section>` therefore carries nothing at
   all: it is the container, and the elements inside it are what reveal.

   Three things are deliberately left out. The two heroes are above the fold on
   every load, so there is no scroll distance to reveal across and the attribute
   could only hold an `h1` at opacity 0 if the bundle failed to run. A marquee is
   already moving under its own loop, so a reveal on it would fight the animation
   it owns: that covers the services ticker and the AI page's model band, which
   share one Marquee. And long-form prose reveals once, as a block: per
   paragraph, an article would be hundreds of scroll targets and would animate as
   noise while somebody is trying to read it. */
import AOS from 'aos';
import { Inertia } from '@inertiajs/inertia';
import { nextTick } from 'vue';

const OPTIONS = {
  /* How far above the viewport's bottom edge a block's top has to sit before it
     reveals. 100px means the block is a little way in rather than revealing
     while it is still clipped by the edge of the screen. */
  offset: 100,

  /* There is deliberately no duration or easing here. The library would only
     write them onto <body> for its own stylesheet to read, and that stylesheet
     no longer draws the reveal: the animation in main.scss owns both, from
     $dur-slow and $ease. */

  /* Reveal once and stay revealed. Re-animating on the way back up makes a long
     page flicker while the reader scrolls, and this motion is meant to arrive
     once, not to keep announcing itself. */
  once: true,

  /* Reveals stay on for every screen. The site shows its motion rather than
     negotiating it away, and this is an animation with no hardware dependency
     behind it, so there is nothing to switch off. */
  disable: false
};

/* The longest line that gets a place of its own. A line longer than this shares
   the last place and arrives together, which bounds the wait: four places at
   $reveal-step is 300ms, well inside the reveal's own length, so a six-across row
   of score gauges never turns into a queue. */
const MAX_PLACES = 4;

/* How far apart two elements can sit and still count as the same line. A row of
   a grid shares one layout offset, so this only absorbs the sub-pixel drift that
   a scaled or flexed layout can leave behind. */
const LINE_TOLERANCE = 4;

/* Where an element sits in the page's layout, ignoring any reveal transform on
   it.

   It cannot use getBoundingClientRect. An element that has not revealed yet is
   painted $reveal-distance below where it rests, so the measured tops of a line
   of cards sit a reveal's travel out, and a line whose members are only a little
   shorter than that travel would read as the line beneath it. offsetTop is the
   layout box, which no transform moves, and walking the offsetParent chain gives
   two elements in different columns the same origin to be compared against. */
function layoutBox(el) {
  let top = 0;
  let left = 0;
  let node = el;

  while (node && !isNaN(node.offsetTop)) {
    top += node.offsetTop;
    left += node.offsetLeft;
    node = node.offsetParent;
  }

  return { top, left };
}

/* Write each waiting element's place in the line it sits on, which is what the
   animation-delay in main.scss multiplies by $reveal-step.

   Only elements that have not arrived are touched. One that has already revealed
   keeps the place it was given, and rewriting it would restart an animation the
   reader has already watched. */
function sequence() {
  const waiting = Array.from(document.querySelectorAll('[data-aos]:not(.aos-animate)'));
  const lines = [];

  waiting.forEach((el) => {
    const box = layoutBox(el);
    const line = lines.find((candidate) => Math.abs(candidate.top - box.top) < LINE_TOLERANCE);

    if (line) {
      line.members.push({ el, left: box.left });
      return;
    }

    lines.push({ top: box.top, members: [{ el, left: box.left }] });
  });

  lines.forEach((line) => {
    line.members
      .sort((a, b) => a.left - b.left)
      .forEach((member, place) => {
        member.el.style.setProperty('--reveal-index', String(Math.min(place, MAX_PLACES)));
      });
  });
}

let started = false;

export function initReveal() {
  if (started) {
    return;
  }

  started = true;

  /* Sequenced before the library's first scan, so the elements it reveals
     immediately, the ones already in view when the page loads, wait their turn
     as well rather than all arriving at once. */
  sequence();

  AOS.init(OPTIONS);

  /* The library writes its reveal duration onto <body> so that its own stylesheet
     can read it, and that rule outranks a scoped component rule. Left in place it
     would force every revealed element's own transitions to the reveal's length,
     so a card whose hover lift is $dur would take $dur-slow instead. The motion
     is an animation now and takes its duration from $dur-slow directly, so the
     attribute is a side effect with nothing behind it. Only init writes it, so
     one removal is enough. */
  document.body.removeAttribute('data-aos-duration');

  /* A refresh is a re-scan, so the incoming page's blocks are picked up without
     the aos-animate class the outgoing page's blocks carried: they start hidden
     and reveal on scroll, which is what makes the reveal replay per page.

     It waits a tick because Vue patches the DOM in its own microtask. For a
     visit that also matters because Inertia resets the scroll position directly
     after dispatching the event, so by the time this runs the new page is in the
     DOM and the viewport is back at the top, which is the state the trigger
     positions have to be measured against.

     Both events are needed. 'success' is a normal visit. 'navigate' is a
     back/forward restore, which does not go through the visit flow and so never
     fires 'success': without it the restored page's blocks would stay hidden
     until the library's own mutation observer noticed the new nodes, a frame or
     two after the page had already been painted. 'navigate' restores the scroll
     position before it fires, so the same tick is correct for both. */
  const refresh = () => nextTick(() => {
    sequence();
    AOS.refreshHard();
  });

  Inertia.on('success', refresh);
  Inertia.on('navigate', refresh);

  /* Images are the one thing that moves a page after first paint. The trigger
     offset of every element is measured at init, and DOMContentLoaded fires
     before the images have occupied their space, so a cover that arrives a
     moment later pushes everything below it down and those elements would
     reveal against a position that was only true while the image was empty.
     One re-scan once the initial images have loaded re-measures them all.

     It is safe against `once`: a re-scan never takes `aos-animate` off an
     element that already has it, so this corrects the offsets of what has not
     revealed yet and leaves everything else exactly as it was. */
  window.addEventListener('load', refresh);

  /* A reflow can regroup the lines, so the places are rewritten when the
     viewport changes. The library is re-measuring its own triggers on the same
     event; this only re-decides which element waits. */
  let resizeTimer;

  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(sequence, 150);
  });
}

/* Settle the target the reader is about to be scrolled to, and everything that
   reveals inside it, so the jump is measured against final positions.

   An element that has not revealed yet is painted $reveal-distance away from
   where it rests. An anchor jump measures the painted position, so a link to a
   section further down the page would land short by that distance for every
   unrevealed element inside it, with the section's own top tucked above the
   viewport.

   It settles the subtree rather than the target alone because the reveal now
   lives on the elements inside a section, not on the section itself. The section
   is a plain container carrying no attribute, so a target-only version would
   find nothing to settle and every in-page anchor would land short.

   Switching the reveal off for the frame the class lands is what makes the
   measurement read the final position instead of the start of a reveal that has
   not run. Reading the rect back forces that recalculation while it is off, and
   restoring the transition afterwards does not start one, because the class is
   already applied and the transform has not changed since.

   Two things are switched off, and only one is restored. The transition belongs
   to the element (a card's hover lift), so it goes back. The animation is the
   reveal itself, and it stays suppressed: it is applied the moment the class
   lands, so restoring it would replay the whole reveal, wait included, under a
   reader who has just been put down on the section.

   It also means a section the reader is taken straight to arrives rather than
   animating in under their eyes, which is the right reading: a reveal is for
   something being scrolled past, not for something being landed on. */
export function settleReveal(el) {
  if (!el || typeof el.querySelectorAll !== 'function') {
    return;
  }

  const targets = [];

  if (typeof el.hasAttribute === 'function' && el.hasAttribute('data-aos')) {
    targets.push(el);
  }

  el.querySelectorAll('[data-aos]').forEach((node) => targets.push(node));

  const pending = targets.filter((node) => !node.classList.contains('aos-animate'));

  if (!pending.length) {
    return;
  }

  pending.forEach((node) => {
    node.style.transition = 'none';
    node.style.animation = 'none';
    node.classList.add('aos-animate');
  });

  /* One forced layout for the whole subtree, rather than a read per element. */
  el.getBoundingClientRect();

  pending.forEach((node) => {
    node.style.transition = '';
  });
}
